import { isNotDefined, getSourcePathAccessor } from "../utils";
import { RSI as defaultOptions } from "./defaultOptionsForComputation";

/**
 * CMO - Chande Momentum Oscillator
 *
 * Measures momentum by comparing the sum of recent gains versus losses.
 * Similar to RSI but uses raw momentum rather than relative strength.
 *
 * Formula:
 * CMO = 100 * (sumMomentum / absSumMomentum)
 * where:
 *   sumMomentum = sum of (price[i] - price[i-1]) over N periods
 *   absSumMomentum = sum of abs(price[i] - price[i-1]) over N periods
 *
 * Range: -100 to +100
 * - Positive values indicate upward momentum
 * - Negative values indicate downward momentum
 */

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const { windowSize, sourcePath } = options;
    const source = getSourcePathAccessor(sourcePath);

    let sumMomentum = 0;
    let absSumMomentum = 0;
    const history = [];

    return data.map((d, i) => {
      if (i < 1) {
        return undefined; // Need at least 2 bars
      }

      const currentVal = source(d, i);
      const prevVal = source(data[i - 1], i - 1);

      if (isNotDefined(currentVal) || isNotDefined(prevVal)) {
        return undefined;
      }

      // Calculate momentum (difference)
      const diff = currentVal - prevVal;
      history.push(diff);
      sumMomentum += diff;
      absSumMomentum += Math.abs(diff);

      // Once we have a full window
      if (history.length === windowSize) {
        // Calculate CMO
        const cmo =
          absSumMomentum === 0 ? 0 : (100 * sumMomentum) / absSumMomentum;

        // Remove oldest value from window
        const old = history.shift();
        sumMomentum -= old;
        absSumMomentum -= Math.abs(old);

        return cmo;
      }

      return undefined;
    });
  }

  calculator.undefinedLength = function () {
    const { windowSize } = options;
    return windowSize; // Need windowSize + 1 bars (one for the initial comparison)
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
